import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { seoArticles } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import type { FC, ReactNode } from 'react';

interface ContentBlock {
  type: 'intro' | 'h2' | 'paragraph' | 'list' | 'faq' | 'cta';
  text?: string;
  items?: string[] | { q: string; a: string }[];
}

const ArticleContent: FC<{ blocks: ContentBlock[] }> = ({ blocks }) => {
  const [openArticleFaq, setOpenArticleFaq] = useState<number | null>(null);

  const renderBlock = (block: ContentBlock, index: number): ReactNode => {
    switch (block.type) {
      case 'intro':
        return (
          <p key={index} className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
            {block.text}
          </p>
        );
      case 'h2':
        return (
          <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
            {block.text}
          </h2>
        );
      case 'paragraph':
        return (
          <p key={index} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
            {block.text}
          </p>
        );
      case 'list':
        return (
          <ul key={index} className="space-y-2 mb-6 ml-1">
            {(block.items as string[])?.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm md:text-base text-foreground-600 leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        );
      case 'faq':
        return (
          <div key={index} className="space-y-3 mb-8 mt-6">
            {(block.items as { q: string; a: string }[])?.map((faq, i) => (
              <div key={i} className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                <button
                  onClick={() => setOpenArticleFaq(openArticleFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50/80 transition-colors"
                >
                  <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openArticleFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openArticleFaq === i && (
                  <div className="px-5 pb-4 animate-scale-in">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      case 'cta':
        return (
          <div key={index} className="my-10 p-6 md:p-8 rounded-xl bg-gradient-to-br from-primary-100 via-background-50 to-accent-100 border border-background-200/70 text-center">
            <p className="text-base md:text-lg text-foreground-800 font-medium mb-5 leading-relaxed">{block.text}</p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary inline-flex px-8 py-3.5 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md transition-all duration-200"
            >
              Kakobuy Spreadsheet
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
          </div>
        );
      default:
        return null;
    }
  };

  return <>{blocks.map((block, i) => renderBlock(block, i))}</>;
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!article) return;

    const scriptId = 'article-jsonld';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.seoDescription,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: 'Kakobuy Polska Forum',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Kakobuy Polska Forum',
      },
      image: article.image,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://kakobuypolska.forum/artykul/${article.slug}`,
      },
      inLanguage: 'pl',
      articleBody: article.content
        .filter((b) => b.type === 'paragraph' || b.type === 'intro')
        .map((b) => b.text)
        .join(' '),
    });
    document.head.appendChild(script);

    return () => {
      const script = document.getElementById(scriptId);
      if (script) script.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="section-container py-32 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mx-auto mb-4">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Artykuł nie znaleziony</h1>
          <p className="text-foreground-500 mb-6">Przepraszamy, ale ten artykuł nie istnieje lub został przeniesiony.</p>
          <Link to="/" className="btn-primary inline-flex px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md">
            ← Powrót do strony głównej
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-12">
        <article className="section-container max-w-4xl">
          <nav className="mb-8 animate-fade-in">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors">
              <i className="ri-arrow-left-line"></i>
              ← Powrót do strony głównej
            </Link>
          </nav>

          <header className="mb-10 animate-fade-in-up">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium">
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-time-line"></i>
                {article.readTime} min czytania
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>

            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              {article.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-5">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 text-xs">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="prose-custom">
            <ArticleContent blocks={article.content} />
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                <i className="ri-arrow-left-line"></i>
                ← Powrót do strony głównej
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-md"
              >
                Sprawdź Kakobuy Spreadsheet
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>

          <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200/70 animate-fade-in-up">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Powiązane artykuły</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {seoArticles.filter((a) => a.slug !== article.slug).map((a) => (
                <Link
                  key={a.slug}
                  to={`/artykul/${a.slug}`}
                  className="p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded bg-primary-100 text-primary-700 text-[10px] font-medium">{a.category}</span>
                    <span className="text-[10px] text-foreground-400">{a.readTime} min</span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-800 leading-snug">{a.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}